import { Box, Typography } from '@mui/material';
import ZoomInfoIcon from 'assets/zoominfo-logo-text.svg';
import CustomCard from 'components/shared/CustomCard/CustomCard';
import CustomModal from 'components/shared/CustomModal/CustomModal';
import SearchInput from 'components/shared/SearchInput';
import SectionLoader from 'components/shared/SectionLoader';
import { useInputWithDebounce } from 'customHook/useInputWithDebounce';
import { useEffect, useState } from 'react';
import { BusinessesService } from 'service/businesses.service';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setAdminSearchModal } from 'store/slices/businessesSlice';
import zeplinColor from 'theme/Color';
import Result from './Result';

const AdminSearch = () => {
  const dispatch = useAppDispatch();
  const { adminSearchModal } = useAppSelector((state) => state.businesses);
  const handleModalClose = () => dispatch(setAdminSearchModal(false));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);

  const { value, debouncedValue, handleChange, clearInput } = useInputWithDebounce('', 500);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!debouncedValue) return;

      try {
        setLoading(true);
        const result = await BusinessesService.getZoomInfoSearchResults({ q: debouncedValue });
        setData(result.companies);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [debouncedValue]);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        setLoading(true);

        const result = await BusinessesService.getZoomInfoRecentSearch();
        setRecent(result.recent);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecent();
  }, []);

  return (
    <CustomModal handleClose={handleModalClose} open={adminSearchModal}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CustomCard
          styles={{
            borderRadius: '14px',
            padding: { xs: '20px', xl: '24px' },
            backgroundColor: zeplinColor.Neutral1,
            width: '520px',
            height: '600px',
            overflow: 'scroll',
            boxShadow: 'rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              marginBottom: '52px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box
                  sx={{
                    width: '119px',
                    height: '32px',
                  }}
                >
                  <img src={ZoomInfoIcon} alt='zoominfo' width={'100%'} height={'100%'} />
                </Box>
              </Box>
              <img
                src={'/assets/close-icon.svg'}
                alt='close'
                style={{ cursor: 'pointer', width: '24px', height: '24px' }}
                onClick={handleModalClose}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                marginBottom: '8px',
              }}
            >
              <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                Find a Company
              </Typography>
              <Typography variant='responsiveBody3' sx={{ color: zeplinColor.Neutral9 }}>
                Search by company name to view insights and add them to your list.
              </Typography>
            </Box>

            <SearchInput value={value} handleChange={handleChange} clearInput={clearInput} />

            {loading ? (
              <SectionLoader />
            ) : (
              <Box>
                {!value ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography variant='responsiveBody3' sx={{ color: zeplinColor.Neutral8 }}>
                      Recent Searches
                    </Typography>
                    {recent &&
                      recent.map((item: any) => (
                        <Result key={item.id} type={'business'} data={item} />
                      ))}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography variant='responsiveBody3' sx={{ color: zeplinColor.Neutral8 }}>
                      Companies
                    </Typography>
                    {data &&
                      data.map((item: any) => (
                        <Result key={item.id} type={'business'} data={item} />
                      ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </CustomCard>
      </Box>
    </CustomModal>
  );
};

export default AdminSearch;
